import { Component, Input, OnInit, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { InputComponent, SelectComponent } from '@beautyapp/form';
import { PackagePaymentMethod, Bank } from '@beautyapp/core';
import { buildResetPatch } from './payment-method-fields';

@Component({
  selector: 'beautyapp-payment-method-block',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    InputComponent,
    SelectComponent,
  ],
  templateUrl: './payment-method-block.component.html',
})
export class PaymentMethodBlockComponent implements OnInit {
  private translate = inject(TranslateService);
  private destroyRef = inject(DestroyRef);

  /** The parent's FormGroup. Typed loosely so any form with the payment
   *  controls (paymentMethod/cash/card/balance/bank/bankTransfer/
   *  bankTransferBank/consignation) can be passed in without TS complaints
   *  about extra controls (e.g. userId, comment in the sell modal). */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input({ required: true }) group!: FormGroup<any>;

  /** Auto-fills the active channel when the method is set (sale price or
   *  owed charge amount). Defaults to 0 so the block renders safely even
   *  when the parent has not yet resolved the amount. */
  @Input() amount = 0;

  banks: { id: Bank; name: string }[] = [
    { id: Bank.TBC, name: 'TBC bank' },
    { id: Bank.BOG, name: 'Bank of Georgia' },
    { id: Bank.Liberty, name: 'Liberty' },
    { id: Bank.Other, name: 'Other' },
  ];

  paymentMethods: { value: PackagePaymentMethod; label: string }[] = [
    { value: 'CASH', label: this.translate.instant('PAYMENTTYPE.1') },
    { value: 'CARD', label: this.translate.instant('PAYMENTTYPE.2') },
    { value: 'MIXED', label: this.translate.instant('PAYMENTTYPE.9') },
    { value: 'BALANCE', label: this.translate.instant('PAYMENTTYPE.5') },
    { value: 'BARTER', label: this.translate.instant('PAYMENTTYPE.6') },
    { value: 'CONSIGNATION', label: this.translate.instant('PAYMENTTYPE.7') },
    { value: 'BANK_TRANSFER', label: this.translate.instant('PAYMENTTYPE.8') },
  ];

  get selectedMethod(): PackagePaymentMethod | null {
    return this.group.get('paymentMethod')?.value ?? null;
  }

  ngOnInit(): void {
    // Patch channel fields whenever the method changes (bug #2892 reset logic).
    this.group
      .get('paymentMethod')!
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((method: PackagePaymentMethod | null) => {
        if (!method) return;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.group.patchValue(buildResetPatch(method, this.amount) as any);
      });

    // Apply an initial patch so the auto-fill channel is set on first render.
    const initial = this.selectedMethod;
    if (initial) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.group.patchValue(buildResetPatch(initial, this.amount) as any);
    }
  }
}
